import { create } from "zustand"
import { track } from "@/lib/analytics"
import { getFeedback, putFeedback, type FeedbackApp, type FeedbackRow, type FeedbackTarget } from "./api"

/** Which response a rating belongs to. `turnKey` is the app's own turn id; `requestId` is what telemetry knows it by. */
export interface FeedbackRef {
  app: FeedbackApp
  sessionId: string
  target: FeedbackTarget
  turnKey?: string
  requestId?: string
}

export interface Entry {
  vote: 1 | -1 | null
  reasons: string[]
  comment: string
  panel: boolean
  /** The panel has been offered for this response: it is never offered again on its own. */
  prompted: boolean
  thanks: "vote" | "sent" | null
  error: boolean
}

const blank: Entry = { vote: null, reasons: [], comment: "", panel: false, prompted: false, thanks: null, error: false }
export const entryKey = (r: FeedbackRef) => `${r.app}|${r.sessionId}|${r.target}|${r.turnKey ?? ""}`
const sessionKey = (app: string, sessionId: string) => `${app}|${sessionId}`

interface State {
  entries: Record<string, Entry>
  loaded: Record<string, true>
  load: (app: FeedbackApp, sessionId: string) => void
  vote: (ref: FeedbackRef, v: 1 | -1) => void
  toggleReason: (ref: FeedbackRef, reason: string) => void
  setComment: (ref: FeedbackRef, text: string) => void
  openNote: (ref: FeedbackRef) => void
  send: (ref: FeedbackRef) => void
  skip: (ref: FeedbackRef) => void
  retry: (ref: FeedbackRef) => void
  reset: () => void
}

const THANKS_MS = 2000
const timers = new Map<string, number>()

export const useFeedbackStore = create<State>((set, get) => {
  const patch = (key: string, p: Partial<Entry>) =>
    set((s) => ({ entries: { ...s.entries, [key]: { ...(s.entries[key] ?? blank), ...p } } }))
  const flashThanks = (key: string, kind: "vote" | "sent") => {
    patch(key, { thanks: kind })
    window.clearTimeout(timers.get(key))
    timers.set(key, window.setTimeout(() => patch(key, { thanks: null }), THANKS_MS))
  }
  const save = (ref: FeedbackRef, thenThanks?: "vote" | "sent") => {
    const key = entryKey(ref)
    const e = get().entries[key] ?? blank
    patch(key, { error: false })
    putFeedback({
      app: ref.app, session_id: ref.sessionId, target: ref.target, vote: e.vote, reasons: e.vote ? e.reasons : [],
      ...(ref.requestId ? { request_id: ref.requestId } : {}),
      ...(ref.turnKey ? { turn_key: ref.turnKey } : {}),
      ...(e.vote && e.comment.trim() ? { comment: e.comment.trim() } : {}),
    }).then(
      () => {
        if (thenThanks) flashThanks(key, thenThanks)
      },
      () => patch(key, { error: true, thanks: null }),
    )
    track("response_feedback", {
      app: ref.app,
      vote: e.vote === 1 ? "up" : e.vote === -1 ? "down" : "cleared",
      reasons_count: e.vote ? e.reasons.length : 0,
      has_comment: Boolean(e.vote && e.comment.trim()),
    })
  }
  return {
    entries: {},
    loaded: {},
    load: (app, sessionId) => {
      const sk = sessionKey(app, sessionId)
      if (get().loaded[sk] || !sessionId) return
      set((s) => ({ loaded: { ...s.loaded, [sk]: true } }))
      getFeedback(app, sessionId).then(
        ({ items }) =>
          set((s) => {
            const entries = { ...s.entries }
            for (const r of items as FeedbackRow[]) {
              const k = entryKey({ app, sessionId, target: r.target, turnKey: r.turn_key ?? undefined })
              // What the person did in this tab since the page opened wins over the stored row.
              if (entries[k]) continue
              entries[k] = { ...blank, vote: r.vote, reasons: r.reasons, comment: r.comment ?? "", prompted: r.vote === -1 }
            }
            return { entries }
          }),
        () => set((s) => { const loaded = { ...s.loaded }; delete loaded[sk]; return { loaded } }),
      )
    },
    vote: (ref, v) => {
      const key = entryKey(ref)
      const e = get().entries[key] ?? blank
      if (e.vote === v) {
        patch(key, { vote: null, reasons: [], comment: "", panel: false, thanks: null })
        return save(ref)
      }
      const open = v === -1 && !e.prompted
      patch(key, { vote: v, panel: open, prompted: e.prompted || open, thanks: null })
      save(ref, v === 1 ? "vote" : undefined)
    },
    toggleReason: (ref, reason) => {
      const key = entryKey(ref)
      const e = get().entries[key] ?? blank
      patch(key, { reasons: e.reasons.includes(reason) ? e.reasons.filter((x) => x !== reason) : [...e.reasons, reason] })
    },
    setComment: (ref, text) => patch(entryKey(ref), { comment: text.slice(0, 1000) }),
    openNote: (ref) => patch(entryKey(ref), { panel: true, prompted: true, thanks: null }),
    send: (ref) => {
      patch(entryKey(ref), { panel: false })
      save(ref, "sent")
    },
    skip: (ref) => patch(entryKey(ref), { panel: false }),
    retry: (ref) => save(ref),
    reset: () => {
      timers.forEach((t) => window.clearTimeout(t))
      timers.clear()
      set({ entries: {}, loaded: {} })
    },
  }
})

export const useEntry = (ref: FeedbackRef): Entry => useFeedbackStore((s) => s.entries[entryKey(ref)] ?? blank)
